import { createError, RelodeApiError } from "@relode/core";
import {
    convertMarketResearchCompensationResponse,
    convertMarketResearchDiversityResponse,
    convertMarketResearchSummaryResponse,
    convertMarketResearchSupplyDemandResponse,
} from "@relode/core/backend/marketResearch";

import { marketResearchPublic } from "../../../../shared/backend/controllers";
// eslint-disable-next-line max-len
import { convertMarketResearchPublicOverviewResponse } from "../../../../shared/backend/converters/convertPublicMarketResearchResponse";
import {
    ClientPublicMarketResearchActionTypes,
    creators,
} from "./clientPublicMarketResearchActionTypes";
import { ClientPublicMarketResearchState } from "./clientPublicMarketResearchState";

export const useClientPublicMarketResearchActions = (
    state: ClientPublicMarketResearchState,
    dispatch: React.Dispatch<ClientPublicMarketResearchActionTypes>
) => {
    const loadMarketResearch = async (token: string) => {
        try {
            dispatch(creators.loadMarketResearch());

            const overview = loadMarketResearchOverview(token);

            const summary = loadMarketResearchSummary(token);

            const compensation = loadMarketResearchCompensation(token);

            const supplyDemand = loadMarketResearchSupplyDemand(token);

            const diversity = loadMarketResearchDiversity(token);

            await Promise.all([
                overview,
                summary,
                compensation,
                supplyDemand,
                diversity,
            ]);

            dispatch(creators.loadMarketResearchSuccess());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                // this function relies on its inner functions to perform necessary error handling
                if (err.isAbort() || err.isClientError()) return;
            }
            dispatch(creators.loadMarketResearchFailure(err));
        }
    };

    const loadMarketResearchOverview = async (token: string) => {
        dispatch(creators.loadMarketResearchOverview());
        try {
            const response =
                await marketResearchPublic.marketResearchPublicService.getOverview(
                    token
                );
            const data = convertMarketResearchPublicOverviewResponse(response);
            dispatch(creators.loadMarketResearchOverviewSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadMarketResearchOverviewFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.ClientPublicMarketResearchActions.loadMarketResearchOverview"
            );
        }
    };

    const loadMarketResearchCompensation = async (token: string) => {
        dispatch(creators.loadMarketResearchCompensation());
        try {
            const response =
                await marketResearchPublic.marketResearchPublicService.getCompensation(
                    token
                );
            const data = convertMarketResearchCompensationResponse(response);
            dispatch(creators.loadMarketResearchCompensationSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(
                        creators.loadMarketResearchCompensationFailure(err)
                    );
                    return;
                }
            }
            throw createError(
                err,
                "client.ClientPublicMarketResearchActions.loadMarketResearchCompensation"
            );
        }
    };

    const loadMarketResearchSupplyDemand = async (token: string) => {
        dispatch(creators.loadMarketResearchSupplyDemand());
        try {
            const response =
                await marketResearchPublic.marketResearchPublicService.getSupplyDemand(
                    token
                );
            const data = convertMarketResearchSupplyDemandResponse(response);
            dispatch(creators.loadMarketResearchSupplyDemandSuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(
                        creators.loadMarketResearchSupplyDemandFailure(err)
                    );
                    return;
                }
            }
            throw createError(
                err,
                "client.ClientPublicMarketResearchActions.loadMarketResearchSupplyDemand"
            );
        }
    };

    const loadMarketResearchDiversity = async (token: string) => {
        dispatch(creators.loadMarketResearchDiversity());
        try {
            const response =
                await marketResearchPublic.marketResearchPublicService.getDiversity(
                    token
                );

            const data = convertMarketResearchDiversityResponse(response);
            dispatch(creators.loadMarketResearchDiversitySuccess(data));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadMarketResearchDiversityFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.ClientPublicMarketResearchActions.loadMarketResearchDiversity"
            );
        }
    };

    const loadMarketResearchSummary = async (token: string) => {
        dispatch(creators.loadMarketResearchSummary());
        try {
            const response =
                await marketResearchPublic.marketResearchPublicService.getSummary(
                    token
                );
            const summary = convertMarketResearchSummaryResponse(response);
            dispatch(creators.loadMarketResearchSummarySuccess(summary));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.loadMarketResearchSummaryFailure(err));
                    return;
                }
            }
            throw createError(
                err,
                "client.ClientPublicMarketResearchActions.loadMarketResearchSummary"
            );
        }
    };

    return {
        loadMarketResearch,
    };
};

export type ClientPublicMarketResearchActions = ReturnType<
    typeof useClientPublicMarketResearchActions
>;
